import firebase from "firebase/app";
import "firebase/firestore";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { validate } from "../../util/types";
import { tReduxAction } from "../types";
import api from "../../util/api";
import { tDetailedCart } from "tmt_api/src/types/types";
import { safeMap } from "../../util/functions";
import { stringNotEmpty } from "src/util/gFormatting";
import { useAppDispatch } from "src/redux";
import { getApiError, postDraft } from "src/util/apiNew";

export const getDraft = (myDraftId: string): tReduxAction => {
  return (dispatch) => {
    api
      .get({ endpoint: `/cart/${myDraftId}` })
      .then((myData) => {
        const myDraftCart: Array<tDetailedCart> = myData.data;
        dispatch({ type: "SET_DRAFT_ORDER", data: myDraftCart });
      })
      .catch(() => {
        dispatch({
          type: "ERROR",
          error: new Error("Could not fetch draft cart"),
        });
      });
  };
};

export const createFromDraft = (myDraftId: string): tReduxAction => {
  return (dispatch) => {
    api
      .post({ endpoint: `/cart/${myDraftId}/create`, body: undefined })
      .then(() => {
        dispatch({
          type: "RIBBON",
          message: "Successfully added carts to checkout",
        });
      })
      .catch(() => {
        dispatch({
          type: "ERROR",
          error: new Error("Could not transfer draft to cart"),
        });
      });
  };
};

export const createSingleDraft = (
  cartId: string,
  title: string,
  description: string,
): tReduxAction<Promise<string>> => {
  return async (dispatch) => {
    let link = "";
    try {
      link = await postDraft(cartId, title, description);
    } catch (e) {
      const error = getApiError(e);
      dispatch({ type: "ERROR", error: new Error(error.message) });
    }
    return link;
  };
};

export const createDraft = (myDescription: string, myTitle: string): tReduxAction<Promise<string | void>> => {
  return async (dispatch) => {
    return api
      .post<string>({
        endpoint: "/cart/createDraft",
        body: { draftDescription: myDescription, draftSellAsSet: false, draftTitle: myTitle },
      })
      .then((response) => response.data)
      .catch(() => {
        dispatch({ type: "RIBBON", message: new Error("Could not create draft") });
      });
  };
};

export const useFetchDraftCarts = (): void => {
  const _dispatch = useAppDispatch();
  const companyRefPath = useSelector((state) => state.UserReducer.profile.companyRefPath);
  const userId = useSelector((state) => state.UserReducer.profile.uid);
  const companyType = useSelector((state) => state.CompanyReducer.type);
  const brandId = useSelector((state) => (state.CompanyReducer.type === "brand" ? state.CompanyReducer.id : ""));

  useEffect(() => {
    if (!companyRefPath || companyType === "retailer") {
      // _dispatch({ type: "RESET_SETTINGS" });
      return undefined;
    }

    _dispatch({
      type: "SET_LOADING_DRAFT_CART",
      loading: true,
    });
    if (companyType === "brand") {
      return firebase
        .firestore()
        .collection("draftCarts")
        .where("brandUid", "==", brandId)
        .where("isDraftRep", "!=", "")
        .onSnapshot((myCartSnapshot) => {
          _dispatch(fetchCartData(myCartSnapshot));
        });
    }
    return firebase
      .firestore()
      .collectionGroup("draftCarts")
      .where("isDraftRep", "==", userId)
      .onSnapshot((myCartSnapshot) => {
        _dispatch(fetchCartData(myCartSnapshot));
      });
  }, [_dispatch, brandId, companyRefPath, companyType, userId]);
};

// Helper Function
export const fetchCartData = (myCartSnapshot: firebase.firestore.QuerySnapshot): tReduxAction => {
  return (dispatch) => {
    const kCartValidator = validate("tDetailedCart");
    dispatch({ type: "SET_DRAFT_ORDER", data: safeMap(myCartSnapshot.docs, kCartValidator) });

    dispatch({
      type: "SET_LOADING_DRAFT_CART",
      loading: false,
    });
  };
};
export const fetchDraftPageData = (
  myCartSnapshot: firebase.firestore.QuerySnapshot,
  draftId: string,
  draftPage?: boolean,
): tReduxAction => {
  return (dispatch) => {
    const kCartValidator = validate("tDetailedCart");
    if (draftPage && stringNotEmpty(draftId)) {
      api.post({ endpoint: `/cart/${draftId}/create`, body: undefined });
    } else {
      dispatch({ type: "SET_DRAFT_ACCEPT_PAGE", data: safeMap(myCartSnapshot.docs, kCartValidator) });
    }

    dispatch({
      type: "SET_LOADING_DRAFT_CART",
      loading: false,
    });
  };
};

export const useFetchDraftFromId = (draftId: string, draftPage?: boolean): void => {
  const _dispatch = useAppDispatch();
  const companyRefPath = useSelector((state) => state.UserReducer.profile.companyRefPath);
  const userId = useSelector((state) => state.UserReducer.profile.uid);
  const companyType = useSelector((state) => state.CompanyReducer.type);
  const brandId = useSelector((state) => (state.CompanyReducer.type === "brand" ? state.CompanyReducer.id : ""));

  useEffect(() => {
    _dispatch({
      type: "SET_LOADING_DRAFT_CART",
      loading: true,
    });
    if (companyType === "brand") {
      return firebase
        .firestore()
        .collection("draftCarts")
        .where("brandUid", "==", brandId)
        .where("draftCartId", "==", draftId)
        .onSnapshot((myCartSnapshot) => {
          _dispatch(fetchDraftPageData(myCartSnapshot, draftId, draftPage));
        });
    }
    return firebase
      .firestore()
      .collectionGroup("draftCarts")
      .where("draftCartId", "==", draftId)
      .onSnapshot((myCartSnapshot) => {
        _dispatch(fetchDraftPageData(myCartSnapshot, draftId, draftPage));
      });
  }, [_dispatch, brandId, companyRefPath, companyType, draftId, draftPage, userId]);
};
